// @ts-nocheck
import { Loading } from '@cibo/profile'
import { Box, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import * as d3 from 'd3'
import { useEffect, useRef } from 'react'
import legendMeta from '../../../../hooks/useHistogramLegend/fixtures/metadata.json'
import { Config } from '../../../../types'
import { computePlantingDateStats } from './computePlantingDateStats'
import { ErrorBarTimeSeriesChart } from './ErrorBarTimeSeriesChart'
import { histogram } from './histogram'

const useCountyData = (fips: string) => {
  const maptilesServiceUrl = Config.get('MAPTILES_SERVICE_URL')

  return useQuery({
    queryKey: ['countyData', fips],
    queryFn: () => {
      return fetch(`${maptilesServiceUrl}db/counties/${fips}`).then(res => res.json())
    },
    enabled: !!fips,
  })
}

interface CountyDataProps {
  countyFeature?: any
  fips?: string | number
}

export const CountyData = ({ countyFeature, fips }: CountyDataProps) => {
  const countyData = useCountyData(`${fips}`)
  const deepCdlHisto = [2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025].map(
    year =>
      Array.isArray(countyData.data) &&
      histogram(countyData.data?.map((d: any) => d[`deepcdl_${year}`]))
  )

  const tillageHisto = [2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025].map(
    year =>
      Array.isArray(countyData.data) &&
      histogram(countyData.data?.map((d: any) => d[`tillage_${year}`]))
  )

  // Covercrop histogram for each year
  const covercropHisto = [2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025].map(
    year =>
      Array.isArray(countyData.data) &&
      histogram(countyData.data?.map((d: any) => d[`covercrop_${year}`]))
  )

  if (!Array.isArray(countyData.data))
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {countyFeature?.properties?.label}
        </Typography>

        <Loading />
      </Box>
    )

  // Compute stats for planting_date and harvest_date
  const plantingStats =
    countyData.data && computePlantingDateStats(countyData.data, 'planting_date')
  const harvestStats = countyData.data && computePlantingDateStats(countyData.data, 'harvest_date')

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {countyFeature?.properties?.label}
      </Typography>
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>
        Deep CDL
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
        <TimeSeriesStackChart
          yearHistograms={deepCdlHisto}
          indexToColor={(() => {
            const indexToColor: Record<string, string> = {}
            for (const key in legendMeta.legend) {
              const entry = legendMeta.legend[key]
              if (entry && entry.index !== undefined && entry.color) {
                indexToColor[String(entry.index)] = entry.color
              }
            }
            return indexToColor
          })()}
        />
        <div style={{ marginLeft: 16, maxHeight: 180, overflowY: 'auto', minWidth: 120 }}>
          {(() => {
            // Compute total counts for each crop index across all years
            const cropCounts: Record<number, number> = {}
            deepCdlHisto.forEach(histo => {
              if (Array.isArray(histo)) {
                histo.forEach(({ value, count }) => {
                  cropCounts[value] = (cropCounts[value] || 0) + count
                })
              }
            })
            // Get top 8 crop indices by count
            const top8 = Object.entries(cropCounts)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 8)
              .map(([index]) => +index)
            return top8.map(index => {
              // Find legend entry by index
              const legendEntry = Object.values(legendMeta.legend).find(
                (entry: any) => entry.index === +index
              )
              if (!legendEntry) return null
              return (
                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      background: legendEntry.color,
                      marginRight: 6,
                    }}
                  />
                  <span style={{ fontSize: 12 }}>{legendEntry.label}</span>
                </div>
              )
            })
          })()}
        </div>
      </div>
      {/* Covercrop Chart */}
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>
        Covercrop
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
        <TimeSeriesStackChart
          yearHistograms={covercropHisto}
          indexToColor={{
            '0': 'rgb(231,138,195)', // No Cover Crop
            '1': 'rgb(166,216,84)',
          }}
        />
        <div style={{ marginLeft: 16, maxHeight: 180, overflowY: 'auto', minWidth: 120 }}>
          {[0, 1].map(index => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
              <div
                style={{
                  width: 16,
                  height: 16,
                  background: index === 0 ? 'rgb(231,138,195)' : 'rgb(166,216,84)',
                  marginRight: 6,
                }}
              />
              <span style={{ fontSize: 12 }}>{index === 0 ? 'No Cover Crop' : 'Cover Crop'}</span>
            </div>
          ))}
        </div>
      </div>
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>
        Tillage
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
        <TimeSeriesStackChart yearHistograms={tillageHisto} indexToColor={tillageIndexToColor} />
        <div style={{ marginLeft: 16, maxHeight: 180, overflowY: 'auto', minWidth: 120 }}>
          {[0, 1, 2].map(idx => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
              <div
                style={{
                  width: 16,
                  height: 16,
                  background: tillageIndexToColor[idx],
                  marginRight: 6,
                }}
              />
              <span style={{ fontSize: 12 }}>Tillage {idx}</span>
            </div>
          ))}
        </div>
      </div>
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>
        Planting Date (avg ± stdev)
      </Typography>
      <ErrorBarTimeSeriesChart stats={plantingStats} label="Planting Date" />
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>
        Harvest Date (avg ± stdev)
      </Typography>
      <ErrorBarTimeSeriesChart stats={harvestStats} label="Harvest Date" />{' '}
    </Box>
  )
}

// Tillage palette: index R G B A
const tillagePalette = `0 102 194 165 255
1 141 160 203 255
2 252 141 98 255`
const tillageIndexToColor: Record<string, string> = {}
tillagePalette.split('\n').forEach(line => {
  const [idx, r, g, b] = line.trim().split(/\s+/)
  if (idx !== undefined && r && g && b) {
    tillageIndexToColor[idx] = `rgb(${r},${g},${b})`
  }
})

interface TimeSeriesStackChartProps {
  yearHistograms: Array<Array<{ value: number; count: number }> | false>
  indexToColor: Record<string, string>
  years?: number[]
  width?: number
  height?: number
}

const TimeSeriesStackChart = ({
  yearHistograms,
  indexToColor,
  years = [2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025],
  width = 320,
  height = 180,
}: TimeSeriesStackChartProps) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  useEffect(() => {
    const timeSeriesStack = yearHistograms.map((histo, i) => {
      const stack: Record<number, number> = {}
      if (Array.isArray(histo)) {
        for (const { value, count } of histo) {
          stack[value] = count
        }
      }
      return { year: years[i], stack }
    })
    const allKeys = Array.from(new Set(timeSeriesStack.flatMap(y => Object.keys(y.stack)))).sort(
      (a, b) => +a - +b
    )
    const data = timeSeriesStack.map(row => {
      const out: any = { year: row.year }
      for (const k of allKeys) out[k] = row.stack[k] || 0
      return out
    })
    const stackGen = d3.stack().keys(allKeys)
    const series = stackGen(data)
    const x = d3
      .scaleBand()
      .domain(years.map(String))
      .range([40, width - 10])
      .padding(0.1)
    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, d => allKeys.reduce((sum, k) => sum + d[k], 0)) || 1])
      .range([height - 30, 10])
    const color = (d: string) => indexToColor[d] || 'transparent'
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', height)
    svg
      .append('g')
      .attr('transform', `translate(0,${height - 30})`)
      .call(d3.axisBottom(x))
    svg
      .append('g')
      .attr('transform', `translate(40,0)`)
      .call(d3.axisLeft(y))
    svg
      .append('g')
      .selectAll('g')
      .data(series)
      .join('g')
      .attr('fill', d => color(d.key))
      .selectAll('rect')
      .data(d => d)
      .join('rect')
      .attr('x', d => x(String(d.data.year)))
      .attr('y', d => y(d[1]))
      .attr('height', d => y(d[0]) - y(d[1]))
      .attr('width', x.bandwidth())
  }, [yearHistograms, indexToColor, years, width, height])

  return <svg ref={svgRef} width={width} height={height} />
}
